import React from "react";
import { BuildingOfficeIcon } from "@heroicons/react/24/outline";
import { StatusBadge } from "../../../../shared/components/badges/StatusBadge";
import CenterExpandRow from "../components/CenterExpandRow";
import TabbedForm from "../../../../shared/components/forms/FormTabs";
import { CenterService } from "../../../../shared/services/centerService";
import { VillageService } from "../../../../shared/services/villageService";
import { TalukaService } from "../../../../shared/services/TalukaService";
import apiService from "../../../../shared/services/apiService";


/* -------------------------
   Option Loaders - Cascading Dropdowns
------------------------- */
// State - First level (fetch unique states from districts)

const getdistrictDropdown = async () => {
  try {
    const res = await apiService.getAll("dropdowns/districts?isDropdown");
    const districts = res?.data?.districts || res?.districts || res?.data || [];
    return districts.map((item) => ({
      value: item.value || item.id || item._id,
      label: item.label || item.name,
    }));
  } catch (error) {
    console.warn("Failed to load districts from dropdowns API", error);
    return [];
  }
};
const getVillageDropdown  = async () =>{
   try {
    const res = await apiService.getAll("villages?isDropdown=true");
    const villages = res?.data?.villages || res?.villages || res?.data || [];
    return villages.map((item) => ({
      value: item.value || item.id || item._id,
      label: item.label || item.name,
    }));
  } catch (error) {
    console.warn("Failed to load villages from dropdowns API", error);
    return [];
  }
}
const gettalukaDropdown = async ()=>{
    try {
      const res = await apiService.getAll('talukas?page=1&limit=100&isDropdown=true');
      const talukas =
        res?.data?.data?.talukas ||
        res?.data?.talukas ||
        res?.data?.data ||
        res?.data ||
        res?.talukas ||
        [];
  
      return talukas.map(t => ({
        value: t.value || t.id || t._id,
        label: t.label || t.name
      }));
    } catch (error) {
      console.warn('Failed to load talukas for filter', error);
      return [];
    }
}

const getStateOptions = async () => {
  try {
    const res = await apiService.getAll("districts");
    const districts = res?.data?.districts || res?.data || [];
    // Extract unique states from districts
    const uniqueStates = [
      ...new Set(districts.map((d) => d.state).filter(Boolean)),
    ];
    return uniqueStates.map((state) => ({
      value: state,
      label: state,
    }));
  } catch (error) {
    console.warn("Failed to load states from districts", error);
    return [];
  }
};

// District - Second level (filter districts by state)
const getDistrictOptions = async (stateId) => {
  if (!stateId) return [];
  try {
    const res = await apiService.getAll("districts");
    const districts = res?.data?.districts || res?.data || [];
    // Filter districts by selected state
    const filteredDistricts = districts.filter((d) => d.state === stateId);
    return filteredDistricts.map((item) => ({
      value: item.id || item._id,
      label: item.name,
    }));
  } catch (error) {
    console.warn("Failed to load districts", error);
    return [];
  }
};

// Taluka - Third level (depends on District)
const getTalukaOptions = async (districtId) => {
  if (!districtId) return [];
  try {
    const res = await apiService.getAll(`dropdowns/talukas/${districtId}`);
    const talukas = res?.data?.talukas || res?.talukas || res?.data || [];
    return talukas.map((item) => ({
      value: item.value || item.id || item._id,
      label: item.label || item.name,
    }));
  } catch (error) {
    console.warn("Failed to load talukas from dropdowns API", error);
    return [];
  }
};

// Village - Fourth level (depends on Taluka)
const getVillageOptions = async (talukaId) => {
  if (!talukaId) return [];
  try {
    const res = await apiService.getAll(`dropdowns/villages/${talukaId}`);
    const villages = res?.data?.villages || res?.villages || res?.data || [];
    return villages.map((item) => ({
      value: item.value || item.id || item._id,
      label: item.label || item.name,
    }));
  } catch (error) {
    console.warn("Failed to load villages from dropdowns API", error);
    return [];
  }
};

const normalizeNotificationEmails = (value) => {
  if (Array.isArray(value)) return value.join(", ");
  return value || "";
};

const parseNotificationEmails = (value) => {
  if (Array.isArray(value)) return value.map((email) => String(email).trim()).filter(Boolean);
  return String(value || "")
    .split(",")
    .map((email) => email.trim())
    .filter(Boolean);
};

/* -------------------------
   Center Config
------------------------- */
export const centerConfig = {
  title: "Vaccination Center",
  icon: BuildingOfficeIcon,
  useUrlNavigation: true,
  basePath: "/admin/centers",
  endpoint: "admin/vaccination-centers",
  customFilters: [
    {
      key: "talukaId",
      label: "Taluka",
      placeholder: "All Talukas",
      optionsLoader: gettalukaDropdown,
    },
    {
      key: "districtId",
      label: "District",
      placeholder: "All Districts",
      optionsLoader: getdistrictDropdown,
    },
     {
      key: "villageId",
      label: "Village",
      placeholder: "All Villages",
      optionsLoader: getVillageDropdown,
    },
  ],

  /* ---------- TABLE COLUMNS ---------- */
  columns: [
    { key: "name", label: "Center Name", render: (item) => item.name || "-" },
    {
      key: "district",
      label: "District",
      render: (item) => item.district?.name || "-",
    },
    // { key: 'taluka', label: 'Taluka', render: item => item.taluka?.name || "-" },
    // { key: 'village', label: 'Village', render: item => item.village?.name || "-" },
    // { key: 'city', label: 'City', render: item => item.city || "-" },
    { key: "phone", label: "Phone", render: (item) => item.phone || "-" },
    {
      key: "status",
      label: "Status",
      render: (item) => <StatusBadge status={item.status} />,
    },
  ],

  /* ---------- FORM FIELDS ---------- */
  formFields: [
    {
      name: "name",
      label: "Vaccination Center Name",
      type: "text",
      required: true,
      placeholder: "Enter Vaccination Center Name",
      section: "basic",
    },
    {
      name: "country",
      label: "Country",
      type: "text",
      required: true,
      placeholder: "India",
      default: "India",
      section: "basic",
    },

    // State - First level
    {
      name: "stateId",
      label: "State",
      type: "select",
      optionsLoader: getStateOptions,
      required: true,
      placeholder: "Select State",
      section: "basic",
      renderValue: (value, item) =>
        item?.state?.name || value?.label || value || "—",
    },

    // District - Depends on State
    {
      name: "districtId",
      label: "District",
      type: "select",
      optionsLoader: getDistrictOptions,
      required: true,
      placeholder: "Please select state first",
      section: "basic",
      dependsOn: "stateId",
      renderValue: (value, item) =>
        item?.district?.name || value?.label || value || "—",
    },

    // Taluka - Depends on District
    {
      name: "talukaId",
      label: "Taluka",
      type: "select",
      optionsLoader: getTalukaOptions,
      // required: true,
      placeholder: "Please select district first",
      section: "basic",
      dependsOn: "districtId",
      renderValue: (value, item) =>
        item?.taluka?.name || value?.label || value || "—",
    },

    // Village - Depends on Taluka
    {
      name: "villageId",
      label: "Village",
      type: "select",
      optionsLoader: getVillageOptions,
      // required: true,
      placeholder: "Please select taluka first",
      section: "basic",
      dependsOn: "talukaId",
      renderValue: (value, item) =>
        item?.village?.name || value?.label || value || "—",
    },

    {
      name: "address",
      label: "Address",
      type: "textarea",
      rows: 2,
      required: true,
      placeholder: "Enter address",
      fullWidth: true,
      section: "basic",
    },
    // { name: 'landmark', label: 'Landmark', type: 'text', placeholder: 'Opposite State Bank', section: 'basic' },
    {
      name: "pincode",
      label: "Pincode",
      type: "text",
      required: true,
      placeholder: "380001",
      section: "basic",
    },
    {
      name: "phone",
      label: "Phone",
      type: "tel",
      required: true,
      placeholder: "9876543210",
      section: "contact",
    },
    {
      name: "email",
      label: "Email",
      type: "email",
      required: true,
      placeholder: "center@email.com",
      section: "contact",
    },
    {
      name: "lowStockAlertEmails",
      label: "Notification Recipients",
      type: "text",
      placeholder: "admin@vaxicare.com, cmo@vaxicare.com",
      tooltip: "Comma-separated recipient emails for this center's low stock alerts",
      fullWidth: true,
      section: "contact",
    },
    {
      name: "licenseNumber",
      label: "License Number",
      type: "text",
      required: true,
      placeholder: "LIC-2025-001",
      section: "contact",
    },
    {
      name: "openingHours",
      label: "Opening Hours",
      type: "text",
      placeholder: "9:00 AM - 6:00 PM (Mon-Sat)",
      section: "contact",
    },
    {
      name: "facilities",
      label: "Facilities",
      type: "textarea",
      rows: 2,
      placeholder: "Cold Storage, Emergency Care, Laboratory",
      fullWidth: true,
      section: "contact",
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      options: [
        { value: "ACTIVE", label: "Active" },
        { value: "INACTIVE", label: "Inactive" },
      ],
      required: true,
      section: "contact",
    },
    // { name: 'notes', label: 'Notes', type: 'textarea', rows: 3, placeholder: 'Additional information', fullWidth: true, section: 'contact' }
  ],

  searchFields: ["name", "phone", "email"],
  formState: { activeTab: "basic" },

  /* ---------- CUSTOM HANDLERS ---------- */
  customHandlers: {
    // Transform API response to ensure top-level id and nested objects
    transformData: (response) => {
      const items = response?.data?.vaccinationCenters || [];
      return Array.isArray(items)
        ? items.map((item) => ({
            ...item,
            id: item.id || item._id,
            state: item.district?.state || null,
            stateId: item.district?.state || "",

            district: item.district || null,
            districtId: item.district?.id || "",

            taluka: item.taluka || null,
            talukaId: item.taluka?.id || "",

            village: item.village || null,
            villageId: item.village?.id || "",

            status: item.status || "ACTIVE",
            lowStockAlertEmails: normalizeNotificationEmails(
              item.lowStockAlertEmails ||
                item.emailNotificationRecipients ||
                item.lowStockRecipients ||
                item.lowStockEmailRecipients
            ),
          }))
        : [];
    },

    // Load single center for view/edit
    onView: async (item) => {
      try {
        const res = await CenterService.getById(item.id || item._id);
        const data = res?.data || res;

        // Load taluka and village objects if IDs are present
        let taluka = null;
        let village = null;
        if (data.talukaId) {
          try {
            const talukaRes = await TalukaService.getById(data.talukaId);
            taluka = talukaRes?.data || talukaRes;
          } catch (error) {
            console.warn("Failed to load taluka", error);
          }
        }
        if (data.villageId) {
          try {
            const villageRes = await VillageService.getById(data.villageId);
            village = villageRes?.data || villageRes;
          } catch (error) {
            console.warn("Failed to load village", error);
          }
        }

        return {
          ...data,
          stateId: data.district?.state || "",
          districtId: data.district?.id || data.district?._id,
          talukaId: data.taluka?.id || data.taluka?._id,
          villageId: data.village?.id || data.village?._id,
          taluka: taluka,
          village: village,
          status: data.status || "ACTIVE",
          country: data.country || "India",
          lowStockAlertEmails: normalizeNotificationEmails(
            data.lowStockAlertEmails ||
              data.emailNotificationRecipients ||
              data.lowStockRecipients ||
              data.lowStockEmailRecipients
          ),
        };
      } catch (error) {
        console.error("Failed to load center", error);
        return item;
      }
    },

    renderFormSections: (
      formFields,
      formData,
      handleFormChange,
      mode,
      renderFormField,
      activeTab,
      setActiveTab,
    ) => {
      const tabsConfig = {
        basic: { label: "Basic Info" },
        contact: { label: "Contact Info" },
      };

      const wrappedRenderFormField = (field) => {
        // Handle State change - clear District, Taluka and Village when state changes
        if (field.name === "stateId") {
          return renderFormField({
            ...field,
            onChange: async (selectedValue) => {
              console.log("Selected State:", selectedValue);
              handleFormChange("stateId", selectedValue);
              // Clear dependent fields when state changes
              handleFormChange("districtId", null);
              handleFormChange("talukaId", null);
              handleFormChange("villageId", null);
            },
          });
        }

        // Handle District change - depends on State, clear Taluka and Village when district changes
        if (field.name === "districtId") {
          const stateId = formData?.stateId?.value || formData?.stateId;
          return renderFormField({
            ...field,
            disabled: !stateId,
            placeholder: stateId
              ? "Select District"
              : "Please select state first",
            optionsLoader: () => getDistrictOptions(stateId),
            onChange: async (selectedValue) => {
              console.log("Selected District:", selectedValue);
              handleFormChange("districtId", selectedValue);
              // Clear dependent fields when district changes
              handleFormChange("talukaId", null);
              handleFormChange("villageId", null);
            },
          });
        }

        // Handle Taluka - depends on District, clear Village when taluka changes
        if (field.name === "talukaId") {
          const districtId =
            formData?.districtId?.value || formData?.districtId;
          return renderFormField({
            ...field,
            disabled: !districtId,
            placeholder: districtId
              ? "Select Taluka"
              : "Please select district first",
            optionsLoader: () => getTalukaOptions(districtId),
            onChange: async (selectedValue) => {
              console.log("Selected Taluka:", selectedValue);
              handleFormChange("talukaId", selectedValue);
              // Clear village when taluka changes
              handleFormChange("villageId", null);
            },
          });
        }

        // Handle Village - depends on Taluka
        if (field.name === "villageId") {
          const talukaId = formData?.talukaId?.value || formData?.talukaId;
          return renderFormField({
            ...field,
            disabled: !talukaId,
            placeholder: talukaId
              ? "Select Village"
              : "Please select taluka first",
            optionsLoader: () => getVillageOptions(talukaId),
          });
        }

        return renderFormField(field);
      };

      return (
        <TabbedForm
          formFields={formFields}
          formData={formData}
          handleFormChange={handleFormChange}
          renderFormField={wrappedRenderFormField}
          tabsConfig={tabsConfig}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      );
    },

    renderExpandedRow: (item) => <CenterExpandRow item={item} />,

    processFormData: (formData) => {
      console.log("Submitting center form data:", formData);
      const payload = {
        name: formData.name,
        stateId: formData.stateId?.value || formData.stateId,
        districtId: formData.districtId?.value || formData.districtId,
        address: formData.address,
        landmark: formData.landmark,
        city: formData.city,
        district: formData.district,
        state: formData.state,
        pincode: formData.pincode,
        country: formData.country || "India",
        phone: formData.phone,
        email: formData.email,
        lowStockAlertEmails: parseNotificationEmails(formData.lowStockAlertEmails),
        licenseNumber: formData.licenseNumber,
        openingHours: formData.openingHours,
        facilities: formData.facilities,
        status: formData.status || "ACTIVE",
        notes: formData.notes,
      };

      // Only include talukaId and villageId if they have values
      if (formData.talukaId) {
        payload.talukaId = formData.talukaId?.value || formData.talukaId;
      }
      if (formData.villageId) {
        payload.villageId = formData.villageId?.value || formData.villageId;
      }

      return payload;
    },
  },
};
